import { Button, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyHashCell } from '$features/report-hub/components/copy-hash-cell';
import {
  formatFileSize,
  REPORT_STATUS_READY,
} from '$features/report-hub/report-hub.utils';
import { useGetRunRecordEvents } from '$features/report-hub/run-record/run-record-detail.api';
import type { RunRecordReportDetailModel } from '$features/report-hub/run-record/run-record-detail.types';
import { RunRecordEvents } from '$features/report-hub/run-record/run-record-events';
import { RunRecordHistoryCharts } from '$features/report-hub/run-record/run-record-history-charts';
import { RunRecordInfoCard } from '$features/report-hub/run-record/run-record-info-card';
import {
  CollapsibleSection,
  type CollapsibleSectionHandle,
} from '$features/report-hub/stability-report/collapsible-section';
import { EventSeverityFilter } from '$features/report-hub/stability-report/severity-filter';
import { useCopyHash } from '$features/report-hub/use-copy-hash';

import './run-record-detail.scss';
import '../stability-report/stability-report-sidecar.scss';

export interface RunRecordDetailProps {
  report: RunRecordReportDetailModel;
  onDelete: () => void;
  onDownload: () => void;
  isDownloading: boolean;
}

/** Delay before scrolling — gives the section time to expand first. */
const SCROLL_AFTER_OPEN_MS = 50;

export function RunRecordDetail({
  report,
  onDelete,
  onDownload,
  isDownloading,
}: RunRecordDetailProps) {
  const { t } = useTranslation();
  const { copiedHashReportId, handleCopyHash } = useCopyHash();
  const eventsSectionRef = useRef<CollapsibleSectionHandle>(null);
  const eventsContainerRef = useRef<HTMLDivElement>(null);
  const [eventsSeverityFilter, setEventsSeverityFilter] = useState<
    EventSeverityFilter | undefined
  >();

  // Fetch the events list at the parent so the info-card badges and the
  // events section share the same data. The badges read counts from this
  // result; the section reads the same query (cache hit).
  const { data: events } = useGetRunRecordEvents(report.id);

  const handleEventTypeClick = useCallback((severity: EventSeverityFilter) => {
    setEventsSeverityFilter(severity);
    eventsSectionRef.current?.open();
    setTimeout(() => {
      eventsContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, SCROLL_AFTER_OPEN_MS);
  }, []);

  return (
    <div>
      <RunRecordInfoCard
        report={report}
        events={events}
        onEventTypeClick={handleEventTypeClick}
      />

      <CollapsibleSection
        iconName="charts"
        title={t('runRecordDetail.sections.historyCharts')}
      >
        <RunRecordHistoryCharts reportId={report.id} />
      </CollapsibleSection>
      <div ref={eventsContainerRef}>
        <CollapsibleSection
          ref={eventsSectionRef}
          iconName="events"
          title={t('runRecordDetail.sections.events')}
        >
          <RunRecordEvents reportId={report.id} initialSeverity={eventsSeverityFilter} />
        </CollapsibleSection>
      </div>
      <div className="stability-sidecar__meta">
        {report.hash && (
          <>
            <span className="font-weight-bold">
              {t('runRecordDetail.metadata.sha256Hash')}
            </span>
            <CopyHashCell
              hash={report.hash}
              reportId={report.id}
              copiedHashReportId={copiedHashReportId}
              onCopy={handleCopyHash}
            />
          </>
        )}
        <span className="font-weight-bold">{t('runRecordDetail.metadata.size')}</span>
        <span>{formatFileSize(report.fileSize)}</span>
      </div>
      <div className="stability-sidecar__footer">
        <Button
          className="stability-sidecar__delete-button"
          variant="tertiary"
          color="danger"
          size="medium"
          leadingIcon={
            <Icon name="delete" size={IconSizeClasses.XSmall} className="bg-red-500" />
          }
          onClick={onDelete}
        >
          {t('runRecordDetail.actions.delete')}
        </Button>
        {report.status === REPORT_STATUS_READY && (
          <Button size="medium" onClick={onDownload} disabled={isDownloading}>
            <Icon name="save" size={IconSizeClasses.XSmall} className="bg-white" />
            <span className="m-left-l">{t('runRecordDetail.actions.download')}</span>
          </Button>
        )}
      </div>
    </div>
  );
}
